import React, { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // Small delay to let MSAL state propagate to React hooks
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only redirect to login if:
    // 1. Not initializing
    // 2. Not authenticated
    // 3. No MSAL interaction in progress
    // 4. No accounts found
    if (
      !isInitializing &&
      !isAuthenticated &&
      inProgress === InteractionStatus.None &&
      accounts.length === 0
    ) {
      console.log('🔒 Not authenticated, redirecting to login...');
      navigate('/login', { replace: true, state: { from: location } });
    } else if (isAuthenticated && accounts.length > 0) {
      console.log('✅ User authenticated:', accounts[0].username);
    }
  }, [isAuthenticated, inProgress, navigate, location, accounts, isInitializing]);

  // Show loading while MSAL is processing or initializing
  if (isInitializing || inProgress !== InteractionStatus.None) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #0078d4',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite',
        }}></div>
        <p style={{ color: '#605e5c', fontSize: '14px' }}>
          {inProgress === InteractionStatus.HandleRedirect ? 'Processing authentication...' : 'Loading...'}
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // If authenticated, render protected content
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Fallback: show loading while redirecting
  return null;
};

export default ProtectedRoute;
